/**
 * Dépôt confidentiel de sujets d'examen — vue formateur.
 *
 * - Liste les sessions ouvertes
 * - Pour chaque matière du formateur : statut (déposé / non), bouton uploader/replacer
 * - Format accepté : PDF ou Word
 */
import { useState, useMemo } from 'react';
import {
  ShieldCheck, Upload, Loader2, X, FileText, Trash2, Lock, AlertTriangle,
  CheckCircle2, FileType2, Calendar,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useMesDepotsSujetsQuery, useDeposerSujetMutation, useAnnulerMonDepotMutation,
  useDemanderModifDepotMutation, useMesDemandesModifQuery,
  type SessionDepot, type DepotSujetItem,
} from '../../services/formateurApi';

const HEURES_VERROU = 48;
const _est_verrouille_48h = (d: DepotSujetItem): boolean => {
  if (!d.date_depot) return false;
  const now = Date.now();
  const dt = new Date(d.date_depot).getTime();
  return (now - dt) >= HEURES_VERROU * 3600 * 1000;
};

export default function DepotSujets() {
  const { data, isLoading, refetch } = useMesDepotsSujetsQuery();
  const [sessionActive, setSessionActive] = useState<number | null>(null);
  const [showUpload, setShowUpload] = useState<{
    session: SessionDepot; matiere?: { id: number; nom: string; niveau: string; option: string };
    existant?: DepotSujetItem;
  } | null>(null);
  const [annuler] = useAnnulerMonDepotMutation();

  // Sélection auto de la 1re session ouverte
  const sessions = data?.sessions_ouvertes || [];
  const sessionCourante = useMemo(
    () => sessions.find(s => s.id === sessionActive) || sessions[0],
    [sessions, sessionActive],
  );

  // Mes dépôts pour la session courante
  const mesDepots = (data?.results || []).filter(
    d => sessionCourante && d.session_id === sessionCourante.id,
  );

  // Complétude calculée côté backend pour la session courante
  const completude = sessionCourante
    ? (data?.completude_par_session?.[sessionCourante.id])
    : null;
  // Privilégier `groupes` (fusion par nom_matiere + niveau — un formateur
  // qui enseigne la même matière dans plusieurs options ne voit qu'UNE tuile
  // avec les classes listées). Fallback sur `items` (par matière individuelle)
  // pour rétro-compat.
  const itemsCompletude = (completude as any)?.groupes
    || completude?.items
    || (data?.mes_matieres || []).map((m: any) => ({
      matiere_id: m.matiere_id,
      matieres_ids: m.matiere_id ? [m.matiere_id] : [],
      matiere_libelle: m.nom_matiere,
      matiere_code: m.code_matiere,
      niveau: m.niveau,
      classe: '',
      classes: m.classe ? [m.classe] : [],
      option: m.option,
      options: m.option ? [m.option] : [],
      semestre: m.semestre,
      nb_deposes: 0,
      nb_requis: sessionCourante?.nombre_sujets_requis_par_matiere || 2,
      complet: false,
      manquant: sessionCourante?.nombre_sujets_requis_par_matiere || 2,
    }));

  const [demanderModif, { isLoading: demandePending }] = useDemanderModifDepotMutation();
  const { data: mesDemandes, refetch: refetchDemandes } = useMesDemandesModifQuery();
  const [modalDemande, setModalDemande] = useState<null | { depot: DepotSujetItem }>(null);
  const [motifDemande, setMotifDemande] = useState('');
  const [msgFeedback, setMsgFeedback] = useState<string | null>(null);

  // Index des autorisations acceptées + fenêtre encore ouverte, par depot_id
  const autorisationsActives = new Map<number, string>(); // depot_id → date d'expiration
  (mesDemandes?.results || []).forEach(dm => {
    if (dm.statut === 'acceptee' && dm.autorisation_expire_le
        && new Date(dm.autorisation_expire_le).getTime() > Date.now()) {
      autorisationsActives.set(dm.depot_id, dm.autorisation_expire_le);
    }
  });
  const demandesEnAttente = new Set<number>(
    (mesDemandes?.results || []).filter(dm => dm.statut === 'en_attente').map(dm => dm.depot_id),
  );

  const handleAnnuler = async (d: DepotSujetItem) => {
    if (!window.confirm(`Annuler le dépôt du sujet « ${d.matiere_libelle} » ?`)) return;
    try {
      await annuler(d.id).unwrap();
      refetch();
    } catch (err: any) {
      if (err?.data?.code === 'verrou_48h') {
        setMsgFeedback(err.data.detail);
        setModalDemande({ depot: d });
      } else {
        alert(err?.data?.detail || 'Erreur');
      }
    }
  };

  const handleSubmitDemande = async () => {
    if (!modalDemande) return;
    if (motifDemande.trim().length < 10) {
      setMsgFeedback('Motif requis (10 caractères minimum).');
      return;
    }
    try {
      await demanderModif({ depot_id: modalDemande.depot.id, motif: motifDemande.trim() }).unwrap();
      setMsgFeedback('Demande envoyée à la scolarité.');
      setModalDemande(null);
      setMotifDemande('');
      refetchDemandes();
      setTimeout(() => setMsgFeedback(null), 5000);
    } catch (err: any) {
      setMsgFeedback(err?.data?.detail || 'Erreur');
    }
  };

  return (
    <FormateurLayout title="Dépôt des sujets d'examen">
      <div className="bg-amber-900/20 border border-amber-700/40 text-amber-200 rounded-xl p-3 mb-3 text-xs">
        <div className="flex items-start gap-2">
          <ShieldCheck size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold mb-0.5">Dépôt confidentiel</div>
            Vos sujets sont chiffrés et accessibles uniquement à la scolarité.
            Ils ne seront imprimés qu'au moment officiel des examens.
            <br />Formats acceptés : <strong>PDF, Word (.docx, .doc)</strong> — max 20 Mo.
          </div>
        </div>
      </div>

      {/* Sélecteur de session */}
      {sessions.length === 0 && !isLoading && (
        <div className="bg-gray-900/40 border border-dashed border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
          <Calendar size={32} className="inline mb-2 opacity-50" />
          <div>Aucune session de dépôt ouverte pour le moment.</div>
          <div className="text-[11px] mt-1">La scolarité ouvrira une session à l'approche des examens.</div>
        </div>
      )}

      {sessions.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-2.5 mb-3">
          <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
            Session active
          </label>
          <div className="flex flex-wrap gap-1.5">
            {sessions.map(s => (
              <button key={s.id} onClick={() => setSessionActive(s.id)}
                className={`px-3 py-1.5 text-xs font-semibold rounded ${
                  sessionCourante?.id === s.id
                    ? s.type_session === 'rattrapage'
                      ? 'bg-orange-600 text-white'
                      : 'bg-amber-600 text-white'
                    : 'bg-gray-800 text-gray-300'
                }`}>
                {s.libelle} {s.type_session === 'rattrapage' ? '(Ratt.)' : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="text-center py-6 text-gray-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      )}

      {/* Liste matières + dépôt — vue par matière avec complétude X/N */}
      {sessionCourante && (
        <>
          {/* Bandeau récap complétude */}
          {completude && completude.total_matieres > 0 && (
            <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
              completude.nb_incompletes === 0
                ? 'bg-emerald-900/20 border-emerald-700/60 text-emerald-200'
                : 'bg-amber-900/20 border-amber-700/60 text-amber-200'
            }`}>
              <div className="flex items-center gap-2">
                {completude.nb_incompletes === 0 ? (
                  <CheckCircle2 size={14} />
                ) : (
                  <AlertTriangle size={14} />
                )}
                <strong>
                  {completude.nb_completes} / {completude.total_matieres} matière(s) complète(s)
                </strong>
                <span className="text-[10px] opacity-80">
                  ({sessionCourante.nombre_sujets_requis_par_matiere || 2} sujet(s) requis par matière —
                  {sessionCourante.type_session === 'rattrapage' ? ' rattrapage' : ' session normale'})
                </span>
              </div>
              {completude.nb_incompletes > 0 && (
                <div className="mt-1 text-[11px] opacity-90">
                  Il vous reste {completude.nb_incompletes} matière(s) à compléter avant la fin du dépôt.
                </div>
              )}
            </div>
          )}

          <h2 className="text-[11px] uppercase tracking-wider font-bold text-gray-400 mb-2 px-1">
            Mes matières ({itemsCompletude.length})
          </h2>
          {itemsCompletude.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              Aucune matière assignée pour ce semestre.
            </div>
          ) : (
            <div className="space-y-2">
              {itemsCompletude.map((m: any) => {
                // Support format "groupe" (matieres_ids liste) et fallback item (matiere_id seul).
                const matieresIds: number[] = m.matieres_ids && m.matieres_ids.length > 0
                  ? m.matieres_ids
                  : (m.matiere_id ? [m.matiere_id] : []);
                const classesList: string[] = m.classes && m.classes.length > 0
                  ? m.classes
                  : (m.classe ? [m.classe] : []);
                const optionsList: string[] = m.options && m.options.length > 0
                  ? m.options
                  : (m.option ? [m.option] : []);
                const rowKey = `${matieresIds.join('-')}-${m.niveau}`;
                // Tous les dépôts (dédupliqués par nom_original) pour l'une des matières du groupe :
                // quand un formateur uploade en multi-options, un même fichier crée N dépôts
                // (un par option). On ne veut afficher chaque fichier qu'UNE fois.
                const depotsTous = mesDepots.filter((d) => matieresIds.includes(d.matiere_id as number));
                const depotsUniquesMap = new Map<string, any>();
                for (const d of depotsTous) {
                  const cle = `${d.nom_original}|${(d as any).type_sujet || ''}`;
                  if (!depotsUniquesMap.has(cle)) depotsUniquesMap.set(cle, d);
                }
                const depots = Array.from(depotsUniquesMap.values());
                return (
                  <div key={rowKey}
                    className={`rounded-lg border p-3 ${
                      m.complet
                        ? 'bg-emerald-900/20 border-emerald-700/60'
                        : m.nb_deposes > 0
                          ? 'bg-amber-900/20 border-amber-700/60'
                          : 'bg-gray-900 border-gray-800'
                    }`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white truncate">{m.matiere_libelle}</div>
                        <div className="text-[10px] text-gray-500 truncate">
                          {m.matiere_code} • {m.niveau} {m.semestre && `• S${m.semestre}`}
                        </div>
                        {/* Classes concernées (tronc commun) */}
                        {classesList.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {classesList.map((c) => (
                              <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300 font-mono">
                                {c}
                              </span>
                            ))}
                          </div>
                        )}
                        {classesList.length === 0 && optionsList.length > 0 && (
                          <div className="text-[10px] text-gray-500 mt-1 truncate">
                            {optionsList.join(' · ')}
                          </div>
                        )}
                      </div>
                      {/* Badges SO/SR séparés — la scolarité attend SO
                          (ordinaire, principal + secours) ET SR (rattrapage)
                          distinctement, un total « X/Y sujets » est trompeur. */}
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="flex items-center gap-1">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                            (m as any).so_complet
                              ? 'bg-emerald-900/50 text-emerald-300'
                              : ((m as any).nb_so_deposes ?? 0) > 0
                                ? 'bg-amber-900/50 text-amber-300'
                                : 'bg-red-900/50 text-red-300'
                          }`}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400" />
                            SO {(m as any).nb_so_deposes ?? m.nb_deposes} / {(m as any).nb_so_requis ?? m.nb_requis}
                          </span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                            (m as any).sr_complet
                              ? 'bg-emerald-900/50 text-emerald-300'
                              : ((m as any).nb_sr_deposes ?? 0) > 0
                                ? 'bg-amber-900/50 text-amber-300'
                                : 'bg-red-900/50 text-red-300'
                          }`}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
                            SR {(m as any).nb_sr_deposes ?? 0} / {(m as any).nb_sr_requis ?? 1}
                          </span>
                        </div>
                        {m.complet && (
                          <span className="text-[9px] font-bold text-emerald-400 inline-flex items-center gap-0.5">
                            <CheckCircle2 size={9} /> Complet
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Liste des dépôts pour cette matière */}
                    {depots.length > 0 && (
                      <div className="mt-2 mb-2 space-y-1">
                        {depots.map((d, idx) => {
                          const verrouille = _est_verrouille_48h(d);
                          const autorisationActive = autorisationsActives.has(d.id);
                          const demandeEnAttente = demandesEnAttente.has(d.id);
                          return (
                          <div key={d.id} className="flex items-center gap-1.5 text-[11px] text-gray-300 bg-black/30 rounded px-2 py-1">
                            <FileType2 size={10} className="flex-shrink-0" />
                            <span className="font-semibold">Sujet {idx + 1} :</span>
                            <span className="truncate flex-1">{d.nom_original}</span>
                            <span className="text-gray-500 text-[10px]">({d.type_fichier.toUpperCase()})</span>
                            {d.statut !== 'debloque' && (
                              verrouille && !autorisationActive ? (
                                demandeEnAttente ? (
                                  <span className="text-amber-400 text-[10px] inline-flex items-center gap-0.5 px-1 py-0.5 bg-amber-900/30 rounded" title="Demande envoyée">
                                    <Loader2 size={10} /> Demande en cours
                                  </span>
                                ) : (
                                  <button onClick={() => { setModalDemande({ depot: d }); setMotifDemande(''); }}
                                    className="text-amber-400 hover:text-amber-300 p-0.5 inline-flex items-center gap-0.5 text-[10px]"
                                    title="Demander l'autorisation de modifier">
                                    <Lock size={11} /> Demander modif
                                  </button>
                                )
                              ) : (
                                <button onClick={() => handleAnnuler(d)}
                                  className="text-gray-400 hover:text-red-400 p-0.5"
                                  title={autorisationActive ? 'Modification autorisée par la scolarité' : 'Supprimer ce dépôt'}>
                                  <Trash2 size={11} />
                                </button>
                              )
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex gap-1.5 mt-1">
                      <button
                        onClick={() => setShowUpload({
                          session: sessionCourante,
                          matiere: {
                            id: matieresIds[0] || m.matiere_id || 0,
                            nom: m.matiere_libelle,
                            niveau: m.niveau,
                            option: optionsList[0] || m.option || '',
                          },
                        })}
                        disabled={m.complet}
                        className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded inline-flex items-center justify-center gap-1.5 ${
                          m.complet
                            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                            : 'bg-amber-600 hover:bg-amber-700 text-white'
                        }`}>
                        <Upload size={11} />
                        {m.complet
                          ? 'Complet (SO + SR déposés)'
                          : `Déposer un sujet`}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showUpload && (
        <ModalDeposer
          session={showUpload.session}
          matierePreset={showUpload.matiere}
          toutesMatieres={completude?.items || (data?.mes_matieres || []).map((m: any) => ({
            matiere_id: m.matiere_id,
            matiere_libelle: m.nom_matiere,
            niveau: m.niveau,
            option: m.option,
          }))}
          onClose={() => setShowUpload(null)}
          onDone={() => { setShowUpload(null); refetch(); }}
        />
      )}

      {/* Modal : demande de modification (verrou 48 h) */}
      {modalDemande && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4"
          onClick={() => !demandePending && setModalDemande(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white inline-flex items-center gap-2">
                <Lock size={16} className="text-amber-400" /> Demander la modification
              </h3>
              <button onClick={() => setModalDemande(null)} className="text-gray-400 hover:text-white"><X size={16} /></button>
            </header>
            <div className="p-4 space-y-3">
              <div className="text-xs text-gray-300">
                Le délai libre de <strong>48 h</strong> après votre dépôt est dépassé. Envoyez
                une demande motivée à la scolarité — elle décide d'accorder ou non une fenêtre
                de modification.
              </div>
              <div className="p-2 bg-gray-800 rounded text-[11px] text-gray-400">
                <div><strong>Matière</strong> : {modalDemande.depot.matiere_libelle}</div>
                <div><strong>Type</strong> : {(modalDemande.depot as any).type_sujet === 'rattrapage' ? 'Rattrapage (SR)' : 'Ordinaire (SO)'}</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Motif *</label>
                <textarea value={motifDemande} onChange={(e) => setMotifDemande(e.target.value)}
                  rows={4} maxLength={2000} placeholder="Expliquez brièvement pourquoi vous souhaitez modifier ce sujet…"
                  className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 resize-none" />
                <div className="text-[10px] text-gray-500 mt-0.5">{motifDemande.length}/2000 caractères (minimum 10)</div>
              </div>
              {msgFeedback && (
                <div className="text-[11px] p-2 bg-red-900/30 border border-red-800 text-red-200 rounded">
                  {msgFeedback}
                </div>
              )}
            </div>
            <footer className="px-4 py-3 border-t border-gray-800 flex justify-end gap-2">
              <button onClick={() => setModalDemande(null)} disabled={demandePending}
                className="px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 rounded">Annuler</button>
              <button onClick={handleSubmitDemande} disabled={demandePending || motifDemande.trim().length < 10}
                className="px-4 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
                {demandePending ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                Envoyer la demande
              </button>
            </footer>
          </div>
        </div>
      )}

      {msgFeedback && !modalDemande && (
        <div className="fixed bottom-4 right-4 z-50 px-4 py-2 bg-blue-600 text-white text-xs rounded shadow-lg">
          {msgFeedback}
        </div>
      )}
    </FormateurLayout>
  );
}

function ModalDeposer({ session, matierePreset, toutesMatieres, onClose, onDone }: {
  session: SessionDepot;
  matierePreset?: { id: number; nom: string; niveau: string; option: string };
  toutesMatieres?: Array<any>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [matiere, setMatiere] = useState(matierePreset?.nom || '');
  const [niveau, setNiveau] = useState(matierePreset?.niveau || 'L1');
  const [option, setOption] = useState(matierePreset?.option || '');
  const [notes, setNotes] = useState('');
  // Deux emplacements de fichier : sujet ordinaire ET/OU sujet rattrapage.
  const [fichierOrdinaire, setFichierOrdinaire] = useState<File | null>(null);
  const [fichierRattrapage, setFichierRattrapage] = useState<File | null>(null);
  const [erreur, setErreur] = useState('');
  const [deposer, { isLoading }] = useDeposerSujetMutation();

  // Détecte toutes les tuiles (matiere_id, option) que ce formateur enseigne
  // pour la MÊME matière (nom_matiere identique) au MÊME niveau. Permet
  // d'uploader une seule fois pour plusieurs options en tronc commun
  // (ex: Programmation C++ en L1RT + L1DI&GL).
  const matieresPartagees = useMemo(() => {
    if (!matierePreset?.nom || !matierePreset?.niveau || !toutesMatieres) return [];
    const nomLower = matierePreset.nom.trim().toLowerCase();
    return toutesMatieres.filter(m =>
      (m.matiere_libelle || '').trim().toLowerCase() === nomLower
      && (m.niveau || '') === matierePreset.niveau
      && m.matiere_id
    );
  }, [matierePreset, toutesMatieres]);

  // Options sélectionnées pour le dépôt groupé (par défaut : toutes)
  const [optionsSelectionnees, setOptionsSelectionnees] = useState<Set<number>>(
    () => new Set(matieresPartagees.map(m => m.matiere_id))
  );
  const toggleOption = (id: number) => {
    setOptionsSelectionnees(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const validExt = (f: File) => {
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    return ['pdf', 'doc', 'docx'].includes(ext);
  };

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!matiere || !niveau) {
      setErreur('Matière et niveau requis');
      return;
    }
    if (!fichierOrdinaire && !fichierRattrapage) {
      setErreur("Ajoutez au moins un fichier (ordinaire ou rattrapage)");
      return;
    }
    for (const f of [fichierOrdinaire, fichierRattrapage].filter(Boolean) as File[]) {
      if (!validExt(f)) {
        setErreur(`Format accepté : PDF, .docx, .doc (fichier « ${f.name} » invalide)`);
        return;
      }
    }
    const uploads: Array<{ file: File; type_sujet: 'ordinaire' | 'rattrapage' }> = [];
    if (fichierOrdinaire) uploads.push({ file: fichierOrdinaire, type_sujet: 'ordinaire' });
    if (fichierRattrapage) uploads.push({ file: fichierRattrapage, type_sujet: 'rattrapage' });

    // matiere_ids[] : toutes les options cochées (au moins 1)
    const matiereIdsList = Array.from(optionsSelectionnees);
    const optionsMap: Record<string, string> = {};
    matieresPartagees.forEach(m => {
      if (optionsSelectionnees.has(m.matiere_id)) {
        optionsMap[String(m.matiere_id)] = m.option || '';
      }
    });

    try {
      for (const u of uploads) {
        const fd = new FormData();
        if (matiereIdsList.length > 1) {
          // Multi-options : envoyer la liste + le mapping option
          fd.append('matiere_ids', JSON.stringify(matiereIdsList));
          fd.append('options_par_matiere', JSON.stringify(optionsMap));
        } else if (matierePreset?.id) {
          fd.append('matiere_id', String(matierePreset.id));
        }
        fd.append('matiere_libelle', matiere);
        fd.append('niveau', niveau);
        fd.append('option', option);
        fd.append('notes_formateur', notes);
        fd.append('type_sujet', u.type_sujet);
        fd.append('fichier', u.file);
        await deposer({ sessionId: session.id, formData: fd }).unwrap();
      }
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur upload');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-base text-white inline-flex items-center gap-2">
              <Upload size={16} className="text-amber-400" /> Déposer un sujet
            </h3>
            <div className="text-[10px] text-gray-500 mt-0.5">{session.libelle}</div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Matière *</label>
            <input value={matiere} onChange={(e) => setMatiere(e.target.value)}
              disabled={!!matierePreset?.nom} required maxLength={200}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 disabled:opacity-70" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Niveau *</label>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
                disabled={!!matierePreset?.niveau}
                className="w-full px-2 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 disabled:opacity-70">
                {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => <option key={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Option</label>
              <input value={option} onChange={(e) => setOption(e.target.value)}
                disabled={!!matierePreset?.option} maxLength={100}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 disabled:opacity-70" />
            </div>
          </div>

          {/* Multi-options : cette matière est-elle partagée entre plusieurs options ? */}
          {matieresPartagees.length > 1 && (
            <div className="rounded-lg border border-blue-800/50 bg-blue-950/30 p-3">
              <div className="text-xs font-semibold text-blue-300 mb-2 flex items-center gap-1.5">
                <ShieldCheck size={12} />
                Tronc commun : {matieresPartagees.length} options concernées
              </div>
              <p className="text-[10px] text-gray-400 mb-2">
                Vous enseignez « {matierePreset?.nom} » dans plusieurs options. Cochez celles
                pour lesquelles ce sujet s'applique — un seul upload suffira.
              </p>
              <div className="space-y-1">
                {matieresPartagees.map(m => (
                  <label key={m.matiere_id} className="flex items-center gap-2 text-xs text-gray-200 cursor-pointer hover:bg-blue-900/30 px-1.5 py-1 rounded">
                    <input
                      type="checkbox"
                      checked={optionsSelectionnees.has(m.matiere_id)}
                      onChange={() => toggleOption(m.matiere_id)}
                      className="rounded border-gray-600 bg-gray-800"
                    />
                    <span className="font-mono text-[10px] text-blue-300">{m.niveau}</span>
                    <span className="flex-1">{m.option || '(sans option)'}</span>
                    {m.matiere_id === matierePreset?.id && (
                      <span className="text-[9px] px-1 rounded bg-blue-800/60 text-blue-200">tuile en cours</span>
                    )}
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-gray-500 mt-1 tabular-nums">
                {optionsSelectionnees.size} option(s) sélectionnée(s) →
                {' '}{optionsSelectionnees.size} dépôt(s) créés
              </div>
            </div>
          )}

          {/* Deux zones d'upload : sujet ORDINAIRE et sujet RATTRAPAGE.
              Le formateur peut renseigner l'un des deux ou les deux à la
              fois. Un POST distinct est envoyé pour chaque fichier. */}
          <div className="space-y-2 rounded-lg border border-gray-700 bg-gray-800/40 p-3">
            <div className="text-[11px] text-gray-400 mb-1">
              Sélectionnez le(s) sujet(s) — vous pouvez déposer les deux à la fois si vous les avez.
            </div>

            {/* Sujet ordinaire */}
            <div className="rounded border border-blue-900/50 bg-blue-950/30 p-2">
              <div className="text-[11px] font-semibold text-blue-300 mb-1 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
                Sujet — Session ordinaire (SO)
              </div>
              <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setFichierOrdinaire(e.target.files?.[0] || null)}
                className="w-full text-xs text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-900/40 file:text-blue-200" />
              {fichierOrdinaire && (
                <div className="text-[10px] text-gray-500 mt-1 truncate">
                  {fichierOrdinaire.name} ({(fichierOrdinaire.size / 1024 / 1024).toFixed(2)} Mo)
                </div>
              )}
            </div>

            {/* Sujet rattrapage */}
            <div className="rounded border border-amber-900/50 bg-amber-950/30 p-2">
              <div className="text-[11px] font-semibold text-amber-300 mb-1 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                Sujet — Session de rattrapage (SR)
              </div>
              <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setFichierRattrapage(e.target.files?.[0] || null)}
                className="w-full text-xs text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-amber-900/40 file:text-amber-200" />
              {fichierRattrapage && (
                <div className="text-[10px] text-gray-500 mt-1 truncate">
                  {fichierRattrapage.name} ({(fichierRattrapage.size / 1024 / 1024).toFixed(2)} Mo)
                </div>
              )}
            </div>

            <div className="text-[10px] text-gray-500">
              PDF ou Word · 20 Mo max chacun · au moins un des deux requis.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">
              Notes pour la scolarité (optionnel)
            </label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={2} maxLength={2000}
              placeholder="Ex : sujet + corrigé joints, instructions particulières…"
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 resize-none" />
          </div>

          {erreur && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 rounded p-2 text-xs">
              <AlertTriangle size={11} className="inline mr-1" /> {erreur}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button
            type="submit"
            disabled={
              isLoading
              || (!fichierOrdinaire && !fichierRattrapage)
              || !matiere
              || (matieresPartagees.length > 1 && optionsSelectionnees.size === 0)
            }
            className="px-4 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5"
          >
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {isLoading
              ? 'Envoi…'
              : matieresPartagees.length > 1 && optionsSelectionnees.size > 1
                ? `Déposer pour ${optionsSelectionnees.size} options`
                : 'Déposer'}
          </button>
        </footer>
      </form>
    </div>
  );
}
